/**
 * @coeldery/chat-composer — 對話輸入區（**一個框貼底**：自動增高 ＋ 框內底行掣）
 *
 * ⭐ **1.4.0（2026-09-22 產品負責人指示；`decisions/023`）**：
 *   「message input box, should be one box always at the bottom, the send button should also be
 *    inside the box, so that the box will use up the whole width. also the message box height will
 *    increase as the message is more than one row … show all the message at once but not scroll
 *    down and up in that little box like now」
 *   · **一個框**（外層 border／圓角）包住 textarea ＋ **底行**
 *   · **自動增高**（數學喺 `height.ts` 純函數）—— 上限 ＝ 螢幕 `maxHeightVh`%（預設 40），
 *     超過才喺框內捲（**唔准一開始就細框內捲**）
 *   · 底行 ＝ 左〔Must-use 兩粒（短標籤）〕＋（父層 `actionSlot`，例：開始寫歌詞）… 右〔▲ 送出〕
 *   · ❌ 唔再有 `rows={2}`／`maxHeight:160px`／`resize:vertical`
 *
 * 血緣：1.0.0（cosingery 1b）→ 1.1.0（Must-use box：揀字彈兩粒掣）→ 1.2.0（**邏輯反轉**：
 *   撳掣 → 揀字 → 再撳退出）→ 1.3.0（字級改 token ＋ 顯示已標記 N 句）→
 *   **1.4.0（一個框 ＋ 自動增高 ＋ 框內底行 ＋ 短標籤掣）**
 *
 * 拆檔（硬規則 B：逐檔 ≤250 行）：型別 → `types.ts`；細件 → `parts.tsx`；高度數學 → `height.ts`
 * ⚠️ 同庫內 `chat-input`（**實驗中**）重疊 → **P-44 已裁決：保留本模組**（有 Must-use 需求）。
 * 規格（`ui-spec.md` §七）：Enter 送出／Shift+Enter 換行；掣熱區 ≥44×44px；`busy` 保留內容；
 *   文字一律 i18n（key 由 props 入）；顏色只用 CSS var。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { composerHeightFor, composerMaxHeight, needsInnerScroll } from './height.ts';
import { MustUseButtons, PinnedChips, SendButton } from './parts.tsx';
import type { ChatComposerProps, PinnedPhrase } from './types.ts';

export type { ChatComposerProps, PinnedPhrase } from './types.ts';

export default function ChatComposer({
  value,
  onChange,
  onSend,
  busy = false,
  disabled = false,
  maxLength = 4000,
  placeholderKey = 'chat.placeholder',
  sendAriaKey,
  sendLabelKey = 'chat.send',
  actionSlot,
  maxHeightVh = 40,
  armed: armedProp,
  onArmedChange,
  pinned = [],
  onMarkPhrase,
  onUnpin,
}: ChatComposerProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLTextAreaElement>(null);
  /** ⭐ 1.2.0：而家「進入」咗邊個模式（`null` ＝ 冇）；撳掣切換
   *  ⭐ 1.5.0：`armed`／`onArmedChange` 有傳就**由父層控制**（同「訊息揀句」共用同一個模式） */
  const [armedInternal, setArmedInternal] = useState<PinnedPhrase['mode'] | null>(null);
  const controlled = armedProp !== undefined;
  const armed = controlled ? armedProp ?? null : armedInternal;
  const setArmed = useCallback(
    (mode: PinnedPhrase['mode'] | null) => {
      if (!controlled) setArmedInternal(mode);
      onArmedChange?.(mode);
    },
    [controlled, onArmedChange],
  );
  const sendLabel = t(sendAriaKey ?? sendLabelKey);

  /** 由 textarea 讀當前選取（太短／太長都當冇揀） */
  const readSelection = useCallback((): string => {
    const el = ref.current;
    if (!el) return '';
    const picked = value.slice(el.selectionStart ?? 0, el.selectionEnd ?? 0).trim();
    return picked.length >= 2 ? picked.slice(0, 40) : '';
  }, [value]);

  /**
   * ⭐ 1.2.0：模式開啟時，**一揀字就即刻標記**（可連續揀）
   *   · `selectionchange`（Chrome 113+／Safari 支援 textarea）→ 手機都可靠
   *   · 另外補 `mouseup`／`keyup` 做後備
   */
  useEffect(() => {
    if (!armed || !onMarkPhrase) return;
    const mark = () => {
      const picked = readSelection();
      if (!picked) return;
      onMarkPhrase({ text: picked, mode: armed });
    };
    document.addEventListener('selectionchange', mark);
    const el = ref.current;
    el?.addEventListener('mouseup', mark);
    el?.addEventListener('keyup', mark);
    return () => {
      document.removeEventListener('selectionchange', mark);
      el?.removeEventListener('mouseup', mark);
      el?.removeEventListener('keyup', mark);
    };
  }, [armed, onMarkPhrase, readSelection]);

  /**
   * ⭐ 1.4.0 **自動增高**：先歸零 → 量 `scrollHeight` → 設高（封頂 = 螢幕 `maxHeightVh`%）。
   *   ⚠️ `window` 唔存在（SSR／測試 render）都要安全 → 用預設 800px 算上限。
   */
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const vh = typeof window === 'undefined' ? 0 : window.innerHeight;
    const max = composerMaxHeight(vh, maxHeightVh);
    el.style.height = 'auto';
    el.style.height = `${composerHeightFor(el.scrollHeight, max)}px`;
    el.style.overflowY = needsInnerScroll(el.scrollHeight, max) ? 'auto' : 'hidden';
  }, [maxHeightVh]);

  useEffect(() => {
    resize();
  }, [value, resize]);

  /** 轉向／改視窗高度 → 上限跟住變 */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  const canSend = !busy && !disabled && value.trim().length > 0;

  return (
    <div
      style={{
        /** ⭐ 1.4.1：**唔准被壓縮**（要「釘住底部」；父親係 flex column） */
        flex: '0 0 auto',
        /* ⭐ 1.4.2：唔再加 `env(safe-area-inset-bottom)` —— 佢係**下面嘅 dock／navbar** 嘅責任；
           重複加會多一格空白（產品負責人 2026-09-22：「at least 2 rows on the bottom wasted」） */
        padding: '6px 12px',
        background: 'var(--color-card)',
        borderTop: '1px solid var(--color-divider)',
      }}
    >
      <PinnedChips pinned={pinned} onUnpin={onUnpin} />

      {/* ⭐ 1.4.0：**一個框**（全闊）＝ textarea ＋ 底行 */}
      <div
        data-testid="composer-box"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          width: '100%',
          padding: '6px 8px',
          border: '1.5px solid var(--color-divider)',
          borderRadius: '20px',
          background: 'var(--color-bg)',
        }}
      >
        <textarea
          ref={ref}
          value={value}
          data-testid="composer-textarea"
          maxLength={maxLength}
          disabled={busy || disabled}
          placeholder={t(placeholderKey)}
          aria-label={t(placeholderKey)}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (canSend) onSend();
            }
          }}
          style={{
            width: '100%',
            minHeight: '40px',
            padding: '2px 4px 0',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: 'var(--color-text)',
            fontFamily: 'inherit',
            fontSize: 'var(--font-size-lyrics, 18px)',
            lineHeight: 1.5,
            resize: 'none',
          }}
        />

        {/* 底行：左＝Must-use 兩粒 ＋ 父層動作；右＝▲ 送出 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {onMarkPhrase ? (
            <MustUseButtons
              armed={armed}
              disabled={busy || disabled}
              onToggle={(mode) => setArmed(armed === mode ? null : mode)}
            />
          ) : null}

          {actionSlot}

          <SendButton label={sendLabel} disabled={!canSend} onClick={onSend} />
        </div>
      </div>
    </div>
  );
}

